/**
 * Parse a payroll / invoice amount cell into a number, exactly as printed —
 * never rounded or partially read.
 *
 * - Strips `$` / `€` / `£`, a `USD` code, thousands `,`, `%`, and whitespace.
 * - Negatives in every notation carrier invoices and payroll exports use:
 *   `-100`, `-$100`, `$-100`, `(100.00)`, `$(100.00)`, `100.00-`, `100.00 CR`,
 *   and a Unicode minus (`−100`). `DR` (debit) is stripped and stays positive.
 * - Empty / null / undefined -> `0`.
 * - A printed "no amount" placeholder — `-`, `–`, `—`, `N/A`, `none`, `nil` —
 *   -> `0`. Invoices commonly print a dash for zero; treating it as NaN used to
 *   wipe out an employee's whole summed amount downstream (aggregateByEmployee).
 * - Anything else that isn't a complete number after stripping -> `NaN`. The
 *   whole cell must be one number: `parseFloat`'s partial reads (`"4/15/2026"`
 *   -> 4, `"2697.38 2697.38"` -> 2697.382697) silently produced wrong amounts.
 *
 * Uses `parseFloat` on the validated string, consistent with the rest of the
 * CAP codebase (we deliberately do not introduce a decimal library here).
 */
const ZERO_PLACEHOLDER_RE = /^(-+|–|—|n\/?a|none|nil)$/i;
const NUMBER_RE = /^[-+]?(\d+(\.\d*)?|\.\d+)$/;

export function parseNumber(value: unknown): number {
	if (value === null || value === undefined) return 0;

	// Unicode minus / dashes used as a sign in front of an amount.
	let s = String(value)
		.trim()
		.replace(/^[−‒–—](?=\s*[$€£(\d.])/, '-');
	if (s === '') return 0;
	if (ZERO_PLACEHOLDER_RE.test(s)) return 0;

	let negative = false;

	// Credit / debit suffix: "946.44 CR" is a credit, "946.44 DR" a debit.
	const suffix = s.match(/(?<=[\d.)\s])\s*(CR|DR)\.?$/i);
	if (suffix) {
		if (suffix[1].toUpperCase() === 'CR') negative = true;
		s = s.slice(0, suffix.index).trim();
	}

	// Currency symbols / code and whitespace, anywhere ("$ (946.44)", "USD 12.25").
	s = s.replace(/\bUSD\b/gi, '').replace(/[$€£\s]/g, '');

	// Accounting-format negative, including a leading sign outside: "(946.44)".
	if (/^[-+]?\(.*\)$/.test(s)) {
		negative = true;
		s = s.replace(/^[-+]?\((.*)\)$/, '$1');
	}

	// Trailing minus: "946.44-".
	if (/^[^-].*-$/.test(s)) {
		negative = true;
		s = s.slice(0, -1);
	}

	s = s.replace(/[,%]/g, '');
	if (s === '') return 0;
	if (!NUMBER_RE.test(s)) return NaN;

	const n = parseFloat(s);
	return negative ? -Math.abs(n) : n;
}

/**
 * Adds two amounts, dropping binary floating-point noise from the result
 * (0.1 + 0.2 = 0.30000000000000004 -> 0.3). 8 decimal places is far below any
 * printed amount's precision, so no real digit is ever rounded away.
 */
export function addAmounts(a: number, b: number): number {
	return Number((a + b).toFixed(8));
}
